import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import * as THREE from "three";
import { initScene, clearGroup, type SceneCtx } from "../three/scene";
import {
  buildDeck,
  type RailColor,
  type RailHeight,
} from "../three/buildDeck";
import type { StairsConfig, Vertex } from "../lib/geometry";

type Props = {
  vertices: Vertex[];
  stairs: StairsConfig;
  railHeightInches: RailHeight;
  color: RailColor;
  railedEdges?: boolean[];
  onZoomChange?: (pct: number) => void;
};

export type Preview3DHandle = {
  zoomBy: (factor: number) => void;
  reset: () => void;
};

const Preview3D = forwardRef<Preview3DHandle, Props>(function Preview3D(
  { vertices, stairs, railHeightInches, color, railedEdges, onZoomChange },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ctxRef = useRef<SceneCtx | null>(null);
  const initialPosRef = useRef<THREE.Vector3 | null>(null);
  const initialTargetRef = useRef<THREE.Vector3 | null>(null);
  const initialDistanceRef = useRef<number>(1);
  const framedRef = useRef(false);
  const userInteractedRef = useRef(false);
  const onZoomChangeRef = useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;

  useEffect(() => {
    if (!containerRef.current) return;
    const ctx = initScene(containerRef.current);
    ctxRef.current = ctx;

    initialPosRef.current = ctx.camera.position.clone();
    initialTargetRef.current = ctx.controls.target.clone();
    initialDistanceRef.current = ctx.camera.position.distanceTo(
      ctx.controls.target,
    );

    const emit = () => {
      const d = ctx.camera.position.distanceTo(ctx.controls.target);
      onZoomChangeRef.current?.(initialDistanceRef.current / d);
    };
    ctx.controls.addEventListener("change", emit);
    emit();

    const onStart = () => {
      userInteractedRef.current = true;
    };
    ctx.controls.addEventListener("start", onStart);

    // Re-fit on container resize until the user starts driving the camera.
    const ro = new ResizeObserver(() => {
      if (userInteractedRef.current) return;
      if (!framedRef.current) return;
      frameContent(ctx);
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      ctx.controls.removeEventListener("change", emit);
      ctx.controls.removeEventListener("start", onStart);
      ctx.dispose();
      ctxRef.current = null;
    };
  }, []);

  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    clearGroup(ctx.content);
    buildDeck(ctx.content, {
      vertices,
      stairs,
      railHeightInches,
      color,
      railedEdges,
    });
    if (!framedRef.current) {
      frameContent(ctx);
      framedRef.current = true;
      initialPosRef.current = ctx.camera.position.clone();
      initialTargetRef.current = ctx.controls.target.clone();
      initialDistanceRef.current = ctx.camera.position.distanceTo(
        ctx.controls.target,
      );
    }
  }, [vertices, stairs, railHeightInches, color, railedEdges]);

  useImperativeHandle(ref, () => ({
    zoomBy(factor: number) {
      const ctx = ctxRef.current;
      if (!ctx) return;
      userInteractedRef.current = true;
      const { camera, controls } = ctx;
      const dir = new THREE.Vector3()
        .subVectors(camera.position, controls.target);
      const d = dir.length();
      const next = Math.max(
        controls.minDistance,
        Math.min(controls.maxDistance, d / factor),
      );
      dir.setLength(next);
      camera.position.copy(controls.target).add(dir);
      controls.update();
    },
    reset() {
      const ctx = ctxRef.current;
      const pos = initialPosRef.current;
      const tgt = initialTargetRef.current;
      if (!ctx || !pos || !tgt) return;
      ctx.camera.position.copy(pos);
      ctx.controls.target.copy(tgt);
      ctx.controls.update();
    },
  }));

  return <div ref={containerRef} className="h-full w-full" />;
});

function frameContent(ctx: SceneCtx) {
  const { camera, controls, content } = ctx;
  const box = new THREE.Box3().setFromObject(content);
  if (box.isEmpty()) return;
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  if (!Number.isFinite(sphere.radius) || sphere.radius <= 0) return;

  const aspect =
    Number.isFinite(camera.aspect) && camera.aspect > 0 ? camera.aspect : 1;
  const vfov = THREE.MathUtils.degToRad(camera.fov);
  const hfov = 2 * Math.atan(Math.tan(vfov / 2) * aspect);
  // Pad portrait viewports a bit more so the deck still fits inside the
  // visible region after the brand logo (top) and toolbar pills (bottom)
  // overlay the canvas. The orbit target stays at the deck centre on every
  // aspect — moving it off-centre on portrait made spinning on mobile feel
  // like the deck was orbiting a point below itself.
  const padding = aspect < 1 ? 1.3 : 1.2;
  const dist = (sphere.radius * padding) / Math.sin(Math.min(vfov, hfov) / 2);

  const dir = new THREE.Vector3().subVectors(camera.position, controls.target);
  const dirOk =
    Number.isFinite(dir.x) &&
    Number.isFinite(dir.y) &&
    Number.isFinite(dir.z) &&
    dir.lengthSq() > 1e-6;
  if (!dirOk) dir.set(22, 16, -24);
  dir.normalize();

  controls.target.copy(sphere.center);
  camera.position.copy(controls.target).addScaledVector(dir, dist);
  // Make sure the framed distance isn't immediately clamped by the orbit
  // controls' own maxDistance, and give the user some headroom past it.
  if (controls.maxDistance < dist * 1.4) controls.maxDistance = dist * 1.4;
  camera.updateProjectionMatrix();
  controls.update();
}

export default Preview3D;
